import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTabHistory } from '@/hooks/useTabHistory';
import {
  Container,
  Typography,
  Button,
  Box,
  Alert,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Toolbar
} from '@mui/material';
import {
  Person as PersonIcon,
  Groups as GroupsIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { Player, playerService } from '@/services/playerService';
import { useAppSelector } from '@/hooks/redux';
import { UserRole, OrganizationRole } from '@/types/enums';
import PlayerInformationTab from '@/components/player/PlayerInformationTab';
import PlayerTeamsTab from '@/components/player/PlayerTeamsTab';
import { authManager } from '@/services/authManager';

const PlayerProfilePage: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  
  // Wizard mode detection
  const wizardMode = playerId === 'new';
  const effectivePlayerId = wizardMode ? null : playerId;
  
  // Get initial tab from URL or default to 'profile'
  const initialTab = (searchParams.get('tab') as 'profile' | 'teams') || 'profile';
  const { currentTab, changeTab } = useTabHistory({ initialTab });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingTab, setPendingTab] = useState<'profile' | 'teams' | null>(null);

  // Fetch player data (skip in wizard mode)
  const { data: player, isLoading, error, refetch } = useQuery<Player>(
    ['player', effectivePlayerId],
    () => playerService.getPlayer(effectivePlayerId!),
    {
      enabled: !!effectivePlayerId && !wizardMode,
      retry: 2,
      staleTime: 1000 * 60 * 5, // 5 minutes
      keepPreviousData: true,
    }
  );

  // Listen for player photo updates to refetch data
  useEffect(() => {
    const handlePlayerPhotoUpdate = () => {
      refetch();
    };

    window.addEventListener('playerPhotoUpdated', handlePlayerPhotoUpdate);
    return () => {
      window.removeEventListener('playerPhotoUpdated', handlePlayerPhotoUpdate);
    };
  }, [refetch]);

  // Update URL when tab changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (currentTab !== 'profile') {
      newSearchParams.set('tab', currentTab);
    } else {
      newSearchParams.delete('tab');
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [currentTab, searchParams, setSearchParams]);

  // Check permissions
  const currentOrgMembership = user?.availableOrganizations?.find(
    org => org.organizationId === user?.currentOrganizationId
  );
  const isOrgOwnerOrAdmin = currentOrgMembership?.role === OrganizationRole.OWNER ||
                           currentOrgMembership?.role === OrganizationRole.ADMIN;
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const isCreator = user?.parentId === player?.parentId;
  // In wizard mode, always allow modification since user is creating their own player
  const canModifyPlayer = wizardMode || isCreator || isOrgOwnerOrAdmin || isSuperAdmin;

  // Wizard mode handlers
  const [wizardPlayerData, setWizardPlayerData] = useState<any>(null);
  const [wizardTeamAssignments, setWizardTeamAssignments] = useState<any[]>([]);
  const [wizardPhotoFile, setWizardPhotoFile] = useState<File | null>(null);
  const [wizardPhotoType, setWizardPhotoType] = useState<'UPLOAD' | 'GRAVATAR'>('UPLOAD');
  const [wizardPhotoPreview, setWizardPhotoPreview] = useState<string | null>(null);

  const handleNextToTeams = (playerData: any) => {
    setWizardPlayerData(playerData);
    changeTab('teams');
  };

  const handleWizardComplete = async (teamAssignments?: any[]) => {
    try {
      console.log('Creating player with data:', wizardPlayerData);
      
      if (!wizardPlayerData) {
        toast.error('Player data is missing. Please go back and fill in the required information.');
        return;
      }

      // Create the player first - combine firstName/lastName to name for API
      const playerApiData = {
        name: `${wizardPlayerData.firstName} ${wizardPlayerData.lastName}`.trim(),
        email: wizardPlayerData.email || null,
        position: wizardPlayerData.position || null,
        jerseyNumber: wizardPlayerData.jerseyNumber || null,
        homeAddressStreet: wizardPlayerData.homeAddressStreet || null,
        homeAddressCity: wizardPlayerData.homeAddressCity || null,
        homeAddressState: wizardPlayerData.homeAddressState || null,
        homeAddressZip: wizardPlayerData.homeAddressZip || null,
        playerColor: wizardPlayerData.playerColor || null
      };

      const response = await playerService.createPlayer(playerApiData);
      
      // Upload profile photo if provided
      if (wizardPhotoFile && wizardPhotoType === 'UPLOAD') {
        try {
          const formData = new FormData();
          formData.append('photo', wizardPhotoFile);
          const photoToken = await authManager.getAccessToken();
          await fetch(`/api/v1/players/${response.id}/profile-photo/upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${photoToken}`
            },
            body: formData
          });
        } catch (photoError) {
          console.error('Failed to upload profile photo:', photoError);
          // Don't fail the entire process for photo upload issues
        }
      } else if (wizardPhotoType === 'GRAVATAR' && playerApiData.email) {
        try {
          const gravatarToken = await authManager.getAccessToken();
          await fetch(`/api/v1/players/${response.id}/profile-photo/gravatar`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${gravatarToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email: playerApiData.email })
          });
        } catch (photoError) {
          console.error('Failed to set Gravatar:', photoError);
          // Don't fail the entire process for photo setup issues
        }
      }
      
      // Then add team assignments if any
      if (teamAssignments && teamAssignments.length > 0) {
        for (const assignment of teamAssignments) {
          // Clean the assignment data to match API schema
          const cleanAssignment = {
            leagueId: assignment.leagueId,
            teamName: assignment.teamName,
            clubId: assignment.clubId || null,
            division: assignment.division || null,
            season: assignment.season || null,
            jerseyNumber: assignment.jerseyNumber || null,
            position: assignment.position || null,
            isStarter: assignment.isStarter || false,
            notes: assignment.notes || null
          };
          await playerService.assignToLeague(response.id, cleanAssignment);
        }
      }
      
      // Navigate to the main players page
      navigate('/players');
    } catch (error: any) {
      console.error('Failed to create player:', error);
      console.error('Error response:', error.response?.data);
      toast.error(`Failed to create player: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleCancel = () => {
    // Clean up any photo preview URLs to prevent memory leaks
    if (wizardPhotoFile) {
      setWizardPhotoFile(null);
    }
    if (wizardPhotoPreview) {
      URL.revokeObjectURL(wizardPhotoPreview);
      setWizardPhotoPreview(null);
    }
    navigate('/players');
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (wizardMode && wizardPhotoPreview) {
        URL.revokeObjectURL(wizardPhotoPreview);
      }
    };
  }, [wizardMode, wizardPhotoPreview]);

  const handleWizardBack = () => {
    changeTab('profile');
  };

  const handleDone = () => {
    navigate('/players');
  };

  const handleTabChange = (newTab: 'profile' | 'teams') => {
    // In wizard mode, prevent switching to teams tab until player is created
    if (wizardMode && newTab === 'teams' && !effectivePlayerId) {
      return;
    }
    
    if (hasUnsavedChanges && currentTab === 'profile') {
      setPendingTab(newTab);
      setShowUnsavedWarning(true);
    } else {
      changeTab(newTab);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, newTab: 'profile' | 'teams') => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTabChange(newTab);
    }
  };

  const handleConfirmTabChange = () => {
    if (pendingTab) {
      changeTab(pendingTab);
      setPendingTab(null);
    }
    setShowUnsavedWarning(false);
  };

  const handleCancelTabChange = () => {
    setPendingTab(null);
    setShowUnsavedWarning(false);
  };

  // Show nothing while loading - the global loading bar shows progress
  if ((isLoading || !player) && !error && !wizardMode) return null;

  if (error && !wizardMode) {
    return (
      <Container maxWidth="lg" sx={{ py: 2, px: { xs: 1.5, sm: 3 } }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load player. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', mt: -8, flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Vertical Sidebar Menu - Desktop only */}
      <Box sx={{ 
        display: { xs: 'none', md: 'block' },
        width: 240, 
        flexShrink: 0, 
        pr: 3, 
        pl: 3, 
        pt: 10 
      }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {wizardMode ? 'New Player' : player?.name || 'Player Profile'}
        </Typography>
        <Box component="nav" role="tablist" aria-label="Player profile sections">
          <Button
            fullWidth
            variant="text"
            sx={{
              justifyContent: 'flex-start',
              mb: 1,
              ...(currentTab === 'profile' && {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              }),
            }}
            onClick={() => handleTabChange('profile')}
            onKeyDown={(e) => handleKeyDown(e, 'profile')}
            role="tab"
            aria-selected={currentTab === 'profile'}
            aria-controls="profile-tabpanel"
            id="profile-tab"
            startIcon={<PersonIcon />}
            endIcon={hasUnsavedChanges && currentTab === 'profile' ? (
              <Chip
                label="•"
                size="small"
                color="warning"
                sx={{ minWidth: 8, height: 16, '& .MuiChip-label': { px: 0.5 } }}
                aria-label="Has unsaved changes"
              />
            ) : undefined}
          >
            Profile
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{
              justifyContent: 'flex-start',
              mb: 1,
              ...(currentTab === 'teams' && {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              }),
            }}
            onClick={() => handleTabChange('teams')}
            onKeyDown={(e) => handleKeyDown(e, 'teams')}
            disabled={wizardMode && !effectivePlayerId}
            role="tab"
            aria-selected={currentTab === 'teams'}
            aria-controls="teams-tabpanel"
            id="teams-tab"
            startIcon={<GroupsIcon />}
          >
            Teams
          </Button>
        </Box>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1,
        minWidth: 0,
        maxWidth: { xs: '100%', md: 800 },
        pl: { xs: 2, md: 3 },
        pr: { xs: 2, md: 3 },
        pt: { xs: 2, md: 10 },
        pb: { xs: 10, md: 3 }
      }}>
        {/* Spacer for mobile app bar */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Toolbar />
        </Box>


        {/* Tab Content */}
        <Box
          role="tabpanel"
          id="profile-tabpanel"
          aria-labelledby="profile-tab"
          hidden={currentTab !== 'profile'}
        >
          {currentTab === 'profile' && (
            <PlayerInformationTab 
              player={wizardMode ? undefined : player} 
              onUpdate={wizardMode ? undefined : refetch} 
              canModify={canModifyPlayer}
              onHasChangesChange={setHasUnsavedChanges}
              wizardMode={wizardMode}
              onNext={wizardMode ? handleNextToTeams : undefined}
              onCancel={wizardMode ? handleCancel : undefined}
              initialData={wizardMode ? wizardPlayerData : undefined}
              onDataChange={wizardMode ? setWizardPlayerData : undefined}
              onPhotoChange={wizardMode ? setWizardPhotoFile : undefined}
              onPhotoTypeChange={wizardMode ? setWizardPhotoType : undefined}
              initialPhotoType={wizardMode ? wizardPhotoType : undefined}
              onPhotoPreviewChange={wizardMode ? setWizardPhotoPreview : undefined}
              initialPhotoPreview={wizardMode ? wizardPhotoPreview : undefined}
            />
          )}
        </Box>
        <Box
          role="tabpanel"
          id="teams-tabpanel"
          aria-labelledby="teams-tab"
          hidden={currentTab !== 'teams'}
        >
          {currentTab === 'teams' && (wizardMode || effectivePlayerId) && (
            <PlayerTeamsTab 
              player={wizardMode ? { name: `${wizardPlayerData?.firstName || ''} ${wizardPlayerData?.lastName || ''}`.trim() } as Player : player!} 
              onUpdate={wizardMode ? undefined : refetch} 
              canModify={canModifyPlayer}
              wizardMode={wizardMode}
              onDone={wizardMode ? handleWizardComplete : handleDone}
              onBack={wizardMode ? handleWizardBack : undefined}
              wizardTeamAssignments={wizardMode ? wizardTeamAssignments : undefined}
              onTeamAssignmentsChange={wizardMode ? setWizardTeamAssignments : undefined}
            />
          )}
        </Box>
      </Box>

      {/* Bottom Navigation - Mobile only */}
      <Paper 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          display: { xs: 'block', md: 'none' },
          zIndex: 1000,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }} 
        elevation={3}
      >
        <BottomNavigation
          value={currentTab}
          onChange={(_, newValue) => {
            handleTabChange(newValue);
          }}
          showLabels
          aria-label="Player profile navigation"
        >
          <BottomNavigationAction 
            label="Profile" 
            value="profile" 
            icon={<PersonIcon />}
            aria-label="Profile tab"
          />
          <BottomNavigationAction 
            label="Teams" 
            value="teams" 
            icon={<GroupsIcon />}
            aria-label="Teams tab"
            disabled={wizardMode && !effectivePlayerId}
          />
        </BottomNavigation>
      </Paper>

      {/* Unsaved Changes Warning Dialog */}
      <Dialog
        open={showUnsavedWarning}
        onClose={handleCancelTabChange}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon />
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes in the Profile tab. If you switch tabs now, your changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTabChange} startIcon={<ArrowBackIcon />}>
            Stay Here
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmTabChange}
            startIcon={<WarningIcon />}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlayerProfilePage;
